import { z } from 'zod/v4';

import { AssemblyTypeEnumRuntype } from '../assembly';
import { QuantityUnitDTORuntypeOld } from '../backendTypes';
import { PartDTORuntype, PartLiteRuntype } from '../part';
import { QuoteRequestLineItemSummaryDTORuntype } from '../quote-request/quoteRequestBackendTypes';
import { SourcingTemplateLeadTimePreferencesRuntype } from '../sourcingTemplate/sourcingTemplateBackendTypes';

export type NegotiationDTO = z.infer<typeof NegotiationDTORuntype>;
export const NegotiationDTORuntype = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
    created_by: z.string().nullable(),
    sourcing_scenarios: z.array(z.string()), // SourcingScenarioId[]
});

export type NegotiationPatchDTO = z.infer<typeof NegotiationPatchDTORuntype>;
export const NegotiationPatchDTORuntype = z.object({
    name: z.string(),
    description: z.string().nullable().optional(),
});

export type NegotiationDemandGroupInputDTO = z.infer<typeof NegotiationDemandGroupInputDTORuntype>;
export const NegotiationDemandGroupInputDTORuntype = z.object({
    name: z.string(),
    description: z.string().nullable().optional(),
    /** Ids of existing demands to attach to this group. */
    demand_ids: z.array(z.string()),
    sourcing_scenarios: z.array(
        z.object({
            sourcing_template_id: z.string(),
            name: z.string(),
            lead_time_preference: SourcingTemplateLeadTimePreferencesRuntype,
        }),
    ),
});

export type NegotiationPostDTO = z.infer<typeof NegotiationPostDTORuntype>;
export const NegotiationPostDTORuntype = NegotiationPatchDTORuntype.extend({
    /** When set, the backend also creates these demand groups and their scenarios (from the templates), atomically. */
    demand_groups: z.array(NegotiationDemandGroupInputDTORuntype).optional(),
});

export type SourcingPartOptionRequestSummaryDTO = z.infer<typeof SourcingPartOptionRequestSummaryDTORuntype>;
export const SourcingPartOptionRequestSummaryDTORuntype = z.object({
    line_item: z.object({
        kind: z.literal('SourcingLineItem'),
        id: z.string(),
    }),
    part_option: z.object({
        kind: z.literal('SourcingLineItem'),
        solution_configuration_id: z.string(),
        part: PartDTORuntype,
    }),
    component_origin: z.string().nullable(),
    part: PartLiteRuntype,
    part_description: z.string().nullable(),
    ipn_description: z.string().nullable(),
    quote_request_line_items: z.array(QuoteRequestLineItemSummaryDTORuntype),
});

export type PartOptionRequestSummaryDTO = z.infer<typeof PartOptionRequestSummaryDTORuntype>;
export const PartOptionRequestSummaryDTORuntype = SourcingPartOptionRequestSummaryDTORuntype;

export enum LeadTimeUnit {
    Days = 'days',
    Weeks = 'weeks',
    Months = 'months',
}

export type DemandDTO = z.infer<typeof DemandDTORuntype>;
export const DemandDTORuntype = z.object({
    id: z.string(),
    context: z.string().nullable(),
    component_ipn_id: z.string().nullable().optional(),
    created_at: z.string(),
    quantity: QuantityUnitDTORuntypeOld,
    delivery_start_date: z.string().nullable(),
    delivery_end_date: z.string().nullable(),
    customer: z
        .object({
            id: z.string(),
            name: z.string(),
        })
        .nullable()
        .optional(),
    supplier_site: z
        .object({
            id: z.string(),
            name: z.string(),
        })
        .nullable()
        .optional(),
    ship_to_site: z
        .object({
            id: z.string(),
            name: z.string(),
        })
        .nullable()
        .optional(),
    assembly: z
        .object({
            id: z.string(),
            name: z.string(),
            assembly_type: z.object({
                type: AssemblyTypeEnumRuntype,
            }),
        })
        .nullable()
        .optional(),
});
